import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import API from "../lib/api";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from "recharts";
import { TrendUp, ChartBar, Users, Warning, DownloadSimple, FileCsv, FilePdf, ShieldCheck, ShieldWarning, CalendarBlank, Plus, Trash, PaperPlaneTilt, ToggleLeft, ToggleRight, BellRinging, EnvelopeOpen, EnvelopeSimple, DeviceMobile } from "@phosphor-icons/react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { fmtDate } from "../lib/dateUtil";

const STATUS_COLORS = {
  draft: "#94a3b8", planned: "#3b82f6", in_progress: "#f59e0b", on_hold: "#f97316",
  awaiting_vendor: "#a855f7", awaiting_approval: "#6366f1", completed: "#22c55e",
  closed: "#64748b", cancelled: "#ef4444", not_started: "#cbd5e1", assigned: "#0ea5e9",
  awaiting_materials: "#eab308", awaiting_review: "#8b5cf6", completed_by_vendor: "#14b8a6",
  approved: "#10b981", rejected: "#ef4444"
};

function ScoreBadge({ score }) {
  const color = score >= 80 ? "text-green-600 dark:text-green-400" : score >= 60 ? "text-amber-600 dark:text-amber-400" : "text-red-600 dark:text-red-400";
  return <span className={`text-lg font-bold font-[Chivo] ${color}`}>{score}</span>;
}

export default function ReportsPage() {
  const [sendingExec, setSendingExec] = useState(false);
  const sendExecReport = async () => {
    setSendingExec(true);
    try {
      const { data } = await API.post("/exec-report/run-now");
      toast.success(data.sent ? `Executive weekly report emailed to ${data.email}` : "Report generated but the email could not be sent");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to send report"); }
    finally { setSendingExec(false); }
  };
  const [projectStatus, setProjectStatus] = useState([]);
  const [taskCompletion, setTaskCompletion] = useState([]);
  const [budgetData, setBudgetData] = useState([]);
  const [vendorPerf, setVendorPerf] = useState([]);
  const [overdueTasks, setOverdueTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exportType, setExportType] = useState("projects");
  const [exporting, setExporting] = useState(false);
  const [complianceData, setComplianceData] = useState(null);
  const [scheduledReports, setScheduledReports] = useState([]);
  const [showCreateReport, setShowCreateReport] = useState(false);
  const [reportForm, setReportForm] = useState({ name: "", report_type: "project_summary", frequency: "weekly", emails: "", enabled: true });
  const [sendingReport, setSendingReport] = useState(null);
  // Phase 268 — Nudge Log
  const [nudgeLog, setNudgeLog] = useState({ days: 7, total: 0, email_sent: 0, sms_sent: 0, opened: 0, open_rate: 0, rows: [] });
  const [nudgeLogDays, setNudgeLogDays] = useState(7);
  const [nudgeLogLoading, setNudgeLogLoading] = useState(false);

  useEffect(() => {
    Promise.all([
      API.get("/reports/project-status").then(r => setProjectStatus(r.data)).catch(() => {}),
      API.get("/reports/task-completion").then(r => setTaskCompletion(r.data)).catch(() => {}),
      API.get("/reports/budget-tracking").then(r => setBudgetData(r.data)).catch(() => {}),
      API.get("/reports/vendor-performance").then(r => setVendorPerf(r.data)).catch(() => {}),
      API.get("/reports/overdue").then(r => setOverdueTasks(r.data)).catch(() => {}),
      API.get("/reports/compliance-analytics").then(r => setComplianceData(r.data)).catch(() => {}),
      API.get("/scheduled-reports").then(r => setScheduledReports(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  const loadNudgeLog = async (days = nudgeLogDays) => {
    setNudgeLogLoading(true);
    try {
      const { data } = await API.get(`/nudge-log?days=${days}`);
      setNudgeLog(data);
    } catch {
      toast.error("Failed to load nudge log");
    } finally {
      setNudgeLogLoading(false);
    }
  };

  useEffect(() => { loadNudgeLog(nudgeLogDays); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [nudgeLogDays]);

  const handleExport = async (format) => {
    setExporting(true);
    try {
      const endpoint = format === "csv" ? "/reports/export/csv" : "/reports/export/pdf";
      const response = await API.get(endpoint, {
        params: { report_type: exportType },
        responseType: "blob"
      });
      const ext = format === "csv" ? "csv" : "pdf";
      const mime = format === "csv" ? "text/csv" : "application/pdf";
      const blob = new Blob([response.data], { type: mime });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${exportType}_report.${ext}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${exportType} report exported as ${format.toUpperCase()}`);
    } catch {
      toast.error(`Failed to export ${format.toUpperCase()}`);
    } finally {
      setExporting(false);
    }
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;

  const projectChartData = projectStatus.map(p => ({
    name: p.status?.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()),
    value: p.count,
    fill: STATUS_COLORS[p.status] || "#94a3b8"
  }));

  const taskChartData = taskCompletion.map(t => ({
    name: t.status?.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()),
    value: t.count,
    fill: STATUS_COLORS[t.status] || "#94a3b8"
  }));

  const budgetChartData = budgetData.filter(b => b.budget > 0).map(b => ({
    name: b.name?.substring(0, 20),
    budget: b.budget || 0,
    actual: b.actual_cost || 0
  }));

  return (
    <div data-testid="reports-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-[Chivo] tracking-tight">Reports & Analytics</h1>
          <p className="text-sm text-muted-foreground mt-1">Performance metrics and project insights</p>
        </div>
        {/* Export Controls */}
        <div className="flex items-center gap-2 flex-wrap" data-testid="export-controls">
          <Select value={exportType} onValueChange={setExportType}>
            <SelectTrigger className="w-36 h-9" data-testid="export-type-select">
              <DownloadSimple size={14} className="mr-1" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="projects">Projects</SelectItem>
              <SelectItem value="tasks">Tasks</SelectItem>
              <SelectItem value="vendors">Suppliers</SelectItem>
              <SelectItem value="work_orders">Work Orders</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={() => handleExport("csv")} disabled={exporting} data-testid="export-csv-btn">
            <FileCsv size={16} className="mr-1" /> CSV
          </Button>
          <Button variant="outline" size="sm" onClick={() => handleExport("pdf")} disabled={exporting} data-testid="export-pdf-btn">
            <FilePdf size={16} className="mr-1" /> PDF
          </Button>
          <Button variant="outline" size="sm" onClick={sendExecReport} disabled={sendingExec} data-testid="send-exec-report-btn">
            <EnvelopeSimple size={16} className="mr-1" /> {sendingExec ? "Sending..." : "Email Weekly Report"}
          </Button>
        </div>
      </div>

      <Tabs defaultValue="overview">
        <TabsList data-testid="reports-tabs">
          <TabsTrigger value="overview"><ChartBar size={14} className="mr-1" /> Overview</TabsTrigger>
          <TabsTrigger value="vendors"><Users size={14} className="mr-1" /> Supplier Performance</TabsTrigger>
          <TabsTrigger value="overdue"><Warning size={14} className="mr-1" /> Overdue</TabsTrigger>
          <TabsTrigger value="compliance" data-testid="reports-tab-compliance"><ShieldCheck size={14} className="mr-1" /> Compliance</TabsTrigger>
          <TabsTrigger value="scheduled" data-testid="reports-tab-scheduled"><CalendarBlank size={14} className="mr-1" /> Scheduled</TabsTrigger>
          <TabsTrigger value="nudge-log" data-testid="reports-tab-nudge-log"><BellRinging size={14} className="mr-1" /> Nudge Log</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="mt-4 space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="border border-border rounded-md p-4 animate-fade-in">
              <h3 className="text-sm font-semibold font-[Chivo] mb-4">Project Status Distribution</h3>
              {projectChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie data={projectChartData} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={2} dataKey="value">
                      {projectChartData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                    </Pie>
                    <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "6px", fontSize: "12px" }} />
                    <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: "11px" }} />
                  </PieChart>
                </ResponsiveContainer>
              ) : <p className="text-center py-12 text-muted-foreground text-sm">No project data yet</p>}
            </div>

            <div className="border border-border rounded-md p-4 animate-fade-in animate-delay-1">
              <h3 className="text-sm font-semibold font-[Chivo] mb-4">Task Status Distribution</h3>
              {taskChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie data={taskChartData} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={2} dataKey="value">
                      {taskChartData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                    </Pie>
                    <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "6px", fontSize: "12px" }} />
                    <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: "11px" }} />
                  </PieChart>
                </ResponsiveContainer>
              ) : <p className="text-center py-12 text-muted-foreground text-sm">No task data yet</p>}
            </div>
          </div>

          <div className="border border-border rounded-md p-4 animate-fade-in animate-delay-2">
            <h3 className="text-sm font-semibold font-[Chivo] mb-4">Budget vs Actual Spend</h3>
            {budgetChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={budgetChartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                  <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "6px", fontSize: "12px" }} />
                  <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: "11px" }} />
                  <Bar dataKey="budget" name="Budget" fill="#1D4ED8" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="actual" name="Actual" fill="#0EA5E9" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <p className="text-center py-12 text-muted-foreground text-sm">No budget data yet. Add budgets to projects to see tracking.</p>}
          </div>
        </TabsContent>

        {/* Supplier Performance Tab */}
        <TabsContent value="vendors" className="mt-4">
          {vendorPerf.length === 0 ? (
            <div className="text-center py-16 border border-dashed border-border rounded-md">
              <p className="text-muted-foreground">No supplier performance data yet. Assign tasks to suppliers to see metrics.</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="border border-border rounded-md overflow-hidden">
                <table className="w-full text-sm" data-testid="vendor-perf-table">
                  <thead>
                    <tr className="border-b border-border bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
                      <th className="text-left px-4 py-3 font-semibold">Supplier</th>
                      <th className="text-center px-4 py-3 font-semibold">Score</th>
                      <th className="text-center px-4 py-3 font-semibold">Tasks</th>
                      <th className="text-center px-4 py-3 font-semibold hidden md:table-cell">Completed</th>
                      <th className="text-center px-4 py-3 font-semibold hidden md:table-cell">Rejected</th>
                      <th className="text-center px-4 py-3 font-semibold hidden lg:table-cell">Completion Rate</th>
                      <th className="text-center px-4 py-3 font-semibold hidden lg:table-cell">Rework Rate</th>
                      <th className="text-center px-4 py-3 font-semibold hidden lg:table-cell">Avg Time (hrs)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {vendorPerf.map(v => (
                      <tr key={v.vendor_id} className="hover:bg-muted/30 transition-colors" data-testid={`vendor-score-${v.vendor_id}`}>
                        <td className="px-4 py-3">
                          <p className="font-medium">{v.vendor_name}</p>
                          <p className="text-xs text-muted-foreground">{v.email}</p>
                        </td>
                        <td className="px-4 py-3 text-center"><ScoreBadge score={v.performance_score} /></td>
                        <td className="px-4 py-3 text-center">{v.total_tasks}</td>
                        <td className="px-4 py-3 text-center hidden md:table-cell text-green-600">{v.completed_tasks}</td>
                        <td className="px-4 py-3 text-center hidden md:table-cell text-red-600">{v.rejected_tasks}</td>
                        <td className="px-4 py-3 text-center hidden lg:table-cell">{v.completion_rate}%</td>
                        <td className="px-4 py-3 text-center hidden lg:table-cell">{v.rework_rate}%</td>
                        <td className="px-4 py-3 text-center hidden lg:table-cell">{v.avg_completion_hours}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="border border-border rounded-md p-4">
                <h3 className="text-sm font-semibold font-[Chivo] mb-4">Performance Scores</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={vendorPerf} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="vendor_name" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                    <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "6px", fontSize: "12px" }} />
                    <Bar dataKey="performance_score" name="Score" fill="#1D4ED8" radius={[4, 4, 0, 0]}>
                      {vendorPerf.map((v, i) => (
                        <Cell key={i} fill={v.performance_score >= 80 ? "#10B981" : v.performance_score >= 60 ? "#F59E0B" : "#EF4444"} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </TabsContent>

        {/* Overdue Tab */}
        <TabsContent value="overdue" className="mt-4">
          {overdueTasks.length === 0 ? (
            <div className="text-center py-16 border border-dashed border-border rounded-md">
              <TrendUp size={32} className="mx-auto text-green-500 mb-2" />
              <p className="text-muted-foreground">No overdue tasks. Great job!</p>
            </div>
          ) : (
            <div className="border border-border rounded-md overflow-hidden">
              <table className="w-full text-sm" data-testid="overdue-table">
                <thead>
                  <tr className="border-b border-border bg-red-50 dark:bg-red-950/30 text-xs uppercase tracking-wider text-muted-foreground">
                    <th className="text-left px-4 py-3 font-semibold">Task</th>
                    <th className="text-left px-4 py-3 font-semibold hidden md:table-cell">Project</th>
                    <th className="text-left px-4 py-3 font-semibold">Status</th>
                    <th className="text-left px-4 py-3 font-semibold hidden md:table-cell">Assigned</th>
                    <th className="text-left px-4 py-3 font-semibold">Due Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {overdueTasks.map(t => (
                    <tr key={t.id} className="hover:bg-muted/30 transition-colors">
                      <td className="px-4 py-3 font-medium">{t.title}</td>
                      <td className="px-4 py-3 hidden md:table-cell text-xs text-muted-foreground">{t.project_name}</td>
                      <td className="px-4 py-3"><span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium status-${t.status}`}>{t.status?.replace(/_/g," ").replace(/\b\w/g,c=>c.toUpperCase())}</span></td>
                      <td className="px-4 py-3 hidden md:table-cell text-xs text-muted-foreground">{t.assigned_to_name || "-"}</td>
                      <td className="px-4 py-3 text-xs text-red-600 font-medium">{fmtDate(t.due_date)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        {/* Compliance Analytics Tab */}
        <TabsContent value="compliance" className="mt-4 space-y-6" data-testid="compliance-analytics-tab">
          {!complianceData ? (
            <div className="text-center py-16 border border-dashed border-border rounded-md">
              <ShieldCheck size={32} className="mx-auto text-muted-foreground mb-2" />
              <p className="text-muted-foreground">No compliance data available yet.</p>
            </div>
          ) : (
            <>
              {/* Health Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="compliance-health-cards">
                <div className="border border-border rounded-md p-3 text-center">
                  <p className="text-2xl font-bold font-[Chivo]">{complianceData.health.total}</p>
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Total Docs</p>
                </div>
                <div className="border border-emerald-200 dark:border-emerald-800 rounded-md p-3 text-center bg-emerald-50/50 dark:bg-emerald-900/10">
                  <p className="text-2xl font-bold font-[Chivo] text-emerald-600 dark:text-emerald-400">{complianceData.health.valid}</p>
                  <p className="text-[10px] uppercase tracking-widest text-emerald-700 dark:text-emerald-500">Valid</p>
                </div>
                <div className="border border-amber-200 dark:border-amber-800 rounded-md p-3 text-center bg-amber-50/50 dark:bg-amber-900/10">
                  <p className="text-2xl font-bold font-[Chivo] text-amber-600 dark:text-amber-400">{complianceData.health.expiring_soon}</p>
                  <p className="text-[10px] uppercase tracking-widest text-amber-700 dark:text-amber-500">Expiring Soon</p>
                </div>
                <div className="border border-red-200 dark:border-red-800 rounded-md p-3 text-center bg-red-50/50 dark:bg-red-900/10">
                  <p className="text-2xl font-bold font-[Chivo] text-red-600 dark:text-red-400">{complianceData.health.expired}</p>
                  <p className="text-[10px] uppercase tracking-widest text-red-700 dark:text-red-500">Expired</p>
                </div>
                <div className="border border-blue-200 dark:border-blue-800 rounded-md p-3 text-center bg-blue-50/50 dark:bg-blue-900/10">
                  <p className="text-2xl font-bold font-[Chivo] text-blue-600 dark:text-blue-400">{complianceData.verification_rate}%</p>
                  <p className="text-[10px] uppercase tracking-widest text-blue-700 dark:text-blue-500">Verified</p>
                </div>
              </div>

              {/* Charts row */}
              <div className="grid md:grid-cols-2 gap-6">
                {/* Health Donut */}
                <div className="border border-border rounded-md p-4" data-testid="compliance-health-chart">
                  <h3 className="text-sm font-semibold font-[Chivo] mb-4">Document Health Distribution</h3>
                  <ResponsiveContainer width="100%" height={260}>
                    <PieChart>
                      <Pie
                        data={[
                          { name: "Valid", value: complianceData.health.valid, fill: "#10B981" },
                          { name: "Expiring Soon", value: complianceData.health.expiring_soon, fill: "#F59E0B" },
                          { name: "Expired", value: complianceData.health.expired, fill: "#EF4444" },
                          { name: "No Expiry", value: complianceData.health.no_expiry, fill: "#94A3B8" },
                        ].filter(d => d.value > 0)}
                        cx="50%" cy="50%" innerRadius={55} outerRadius={95} paddingAngle={3} dataKey="value"
                      >
                        {[
                          { fill: "#10B981" }, { fill: "#F59E0B" }, { fill: "#EF4444" }, { fill: "#94A3B8" }
                        ].filter((_, i) => [complianceData.health.valid, complianceData.health.expiring_soon, complianceData.health.expired, complianceData.health.no_expiry][i] > 0)
                          .map((e, i) => <Cell key={i} fill={e.fill} />)}
                      </Pie>
                      <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "6px", fontSize: "12px" }} />
                      <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: "11px" }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                {/* Expiry Timeline */}
                <div className="border border-border rounded-md p-4" data-testid="compliance-expiry-timeline">
                  <h3 className="text-sm font-semibold font-[Chivo] mb-4">Expiry Timeline (Next 6 Months)</h3>
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={complianceData.expiry_timeline} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="month" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                      <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "6px", fontSize: "12px" }} />
                      <Bar dataKey="count" name="Docs Expiring" fill="#F59E0B" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Supplier Coverage Matrix */}
              {complianceData.vendor_coverage.length > 0 && (
                <div className="border border-border rounded-md overflow-hidden" data-testid="vendor-coverage-matrix">
                  <div className="px-4 py-3 border-b border-border">
                    <h3 className="text-sm font-semibold font-[Chivo]">Supplier Compliance Coverage</h3>
                    <p className="text-[10px] text-muted-foreground mt-0.5">Document types tracked per supplier with status indicators</p>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="coverage-table">
                      <thead>
                        <tr className="border-b border-border bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
                          <th className="text-left px-4 py-2.5 font-semibold sticky left-0 bg-muted/50">Supplier</th>
                          <th className="text-center px-3 py-2.5 font-semibold">Total</th>
                          <th className="text-center px-3 py-2.5 font-semibold">Verified</th>
                          {complianceData.doc_types.map(dt => (
                            <th key={dt} className="text-center px-3 py-2.5 font-semibold whitespace-nowrap">{dt.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {complianceData.vendor_coverage.map(v => (
                          <tr key={v.vendor_id} className="hover:bg-muted/30 transition-colors" data-testid={`coverage-row-${v.vendor_id}`}>
                            <td className="px-4 py-2.5 font-medium text-xs sticky left-0 bg-card">{v.vendor_name}</td>
                            <td className="text-center px-3 py-2.5 text-xs">{v.total_docs}</td>
                            <td className="text-center px-3 py-2.5 text-xs">{v.verified_count}</td>
                            {complianceData.doc_types.map(dt => {
                              const status = v.doc_types[dt];
                              const cfg = {
                                valid: { dot: "bg-emerald-500", tip: "Valid" },
                                expiring_soon: { dot: "bg-amber-500", tip: "Expiring Soon" },
                                expired: { dot: "bg-red-500", tip: "Expired" },
                                no_expiry: { dot: "bg-slate-400", tip: "No Expiry" },
                              };
                              const c = cfg[status];
                              return (
                                <td key={dt} className="text-center px-3 py-2.5">
                                  {c ? (
                                    <span className={`inline-block h-2.5 w-2.5 rounded-full ${c.dot}`} title={c.tip} />
                                  ) : (
                                    <span className="text-[10px] text-muted-foreground">-</span>
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Recently Expired */}
              {complianceData.recently_expired?.length > 0 && (
                <div className="border border-red-200 dark:border-red-800 rounded-md p-4" data-testid="recently-expired-section">
                  <h3 className="text-sm font-semibold text-red-700 dark:text-red-400 mb-3 flex items-center gap-1.5">
                    <ShieldWarning size={16} /> Recently Expired (Last 30 Days)
                  </h3>
                  <div className="space-y-2">
                    {complianceData.recently_expired.map(d => (
                      <div key={d.id} className="flex items-center justify-between text-xs p-2 bg-red-50 dark:bg-red-900/20 rounded">
                        <div>
                          <span className="font-medium capitalize">{d.doc_type?.replace(/_/g, " ")}</span>
                          <span className="text-muted-foreground ml-2">{d.vendor_name}</span>
                        </div>
                        <span className="text-red-600 dark:text-red-400 font-medium">{d.days_expired} days ago</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </TabsContent>

        {/* Scheduled Reports Tab */}
        <TabsContent value="scheduled" className="mt-4 space-y-6" data-testid="scheduled-reports-tab">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold font-[Chivo]">Scheduled Reports</h3>
              <p className="text-[10px] text-muted-foreground">Configure automated report delivery via email</p>
            </div>
            <Button size="sm" onClick={() => { setReportForm({ name: "", report_type: "project_summary", frequency: "weekly", emails: "", enabled: true }); setShowCreateReport(true); }} data-testid="create-scheduled-report-btn">
              <Plus size={14} className="mr-1" /> New Schedule
            </Button>
          </div>

          {scheduledReports.length === 0 ? (
            <div className="text-center py-16 border border-dashed border-border rounded-md" data-testid="no-scheduled-reports">
              <CalendarBlank size={32} className="mx-auto text-muted-foreground mb-2" />
              <p className="text-muted-foreground mb-1">No scheduled reports</p>
              <p className="text-xs text-muted-foreground">Create a schedule to automatically send reports to stakeholders.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {scheduledReports.map(r => (
                <div key={r.id} className={`border rounded-md p-4 transition-colors ${r.enabled ? "border-border" : "border-border/50 opacity-60"}`} data-testid={`scheduled-report-${r.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="text-sm font-semibold">{r.name}</h4>
                        <Badge variant="secondary" className="text-[9px] capitalize">{r.report_type?.replace(/_/g, " ")}</Badge>
                        <Badge variant={r.enabled ? "default" : "outline"} className="text-[9px]">{r.enabled ? "Active" : "Paused"}</Badge>
                      </div>
                      <div className="flex items-center gap-4 text-[11px] text-muted-foreground">
                        <span>Frequency: <span className="font-medium text-foreground capitalize">{r.frequency}</span></span>
                        <span>Recipients: <span className="font-medium text-foreground">{(r.emails || []).length}</span></span>
                        {r.last_sent && <span>Last sent: <span className="font-medium text-foreground">{new Date(r.last_sent).toLocaleDateString()}</span></span>}
                      </div>
                      {(r.emails || []).length > 0 && (
                        <p className="text-[10px] text-muted-foreground mt-1 truncate">{r.emails.join(", ")}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <Button variant="ghost" size="icon" className="h-7 w-7" title="Send Now" disabled={sendingReport === r.id}
                        onClick={async () => {
                          setSendingReport(r.id);
                          try {
                            const { data } = await API.post(`/scheduled-reports/${r.id}/send-now`);
                            toast.success(data.message);
                            API.get("/scheduled-reports").then(({ data }) => setScheduledReports(data));
                          } catch { toast.error("Failed to send"); }
                          finally { setSendingReport(null); }
                        }} data-testid={`send-now-${r.id}`}>
                        <PaperPlaneTilt size={14} className={sendingReport === r.id ? "animate-spin" : ""} />
                      </Button>
                      <Switch checked={r.enabled} onCheckedChange={async (v) => {
                        try {
                          await API.put(`/scheduled-reports/${r.id}`, { enabled: v });
                          setScheduledReports(prev => prev.map(x => x.id === r.id ? { ...x, enabled: v } : x));
                          toast.success(v ? "Report enabled" : "Report paused");
                        } catch { toast.error("Failed to update"); }
                      }} data-testid={`toggle-report-${r.id}`} />
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive"
                        onClick={async () => {
                          try {
                            await API.delete(`/scheduled-reports/${r.id}`);
                            setScheduledReports(prev => prev.filter(x => x.id !== r.id));
                            toast.success("Report deleted");
                          } catch { toast.error("Failed to delete"); }
                        }} data-testid={`delete-report-${r.id}`}>
                        <Trash size={14} />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Create Report Dialog */}
          <Dialog open={showCreateReport} onOpenChange={setShowCreateReport}>
            <DialogContent className="max-w-md" data-testid="create-report-dialog">
              <DialogHeader>
                <DialogTitle className="font-[Chivo]">New Scheduled Report</DialogTitle>
                <DialogDescription>Configure an automated report to be emailed on a schedule.</DialogDescription>
              </DialogHeader>
              <form onSubmit={async (e) => {
                e.preventDefault();
                try {
                  const emails = reportForm.emails.split(",").map(e => e.trim()).filter(Boolean);
                  await API.post("/scheduled-reports", { ...reportForm, emails });
                  toast.success("Scheduled report created");
                  setShowCreateReport(false);
                  const { data } = await API.get("/scheduled-reports");
                  setScheduledReports(data);
                } catch { toast.error("Failed to create report"); }
              }} className="space-y-4">
                <div><Label>Report Name *</Label><Input value={reportForm.name} onChange={e => setReportForm({...reportForm, name: e.target.value})} required placeholder="e.g. Weekly Summary" data-testid="report-name-input" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Report Type</Label>
                    <Select value={reportForm.report_type} onValueChange={v => setReportForm({...reportForm, report_type: v})}>
                      <SelectTrigger data-testid="report-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="project_summary">Project Summary</SelectItem>
                        <SelectItem value="task_status">Task Status</SelectItem>
                        <SelectItem value="budget_overview">Budget Overview</SelectItem>
                        <SelectItem value="compliance_summary">Compliance Summary</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Frequency</Label>
                    <Select value={reportForm.frequency} onValueChange={v => setReportForm({...reportForm, frequency: v})}>
                      <SelectTrigger data-testid="report-freq-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="daily">Daily</SelectItem>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div><Label>Email Recipients (comma-separated) *</Label><Input value={reportForm.emails} onChange={e => setReportForm({...reportForm, emails: e.target.value})} required placeholder="user@example.com, user2@example.com" data-testid="report-emails-input" /></div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setShowCreateReport(false)}>Cancel</Button>
                  <Button type="submit" data-testid="submit-scheduled-report-btn">Create Schedule</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* Phase 268 — Nudge Log Tab */}
        <TabsContent value="nudge-log" className="mt-4 space-y-4" data-testid="nudge-log-tab">
          {/* Summary strip + range selector */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap gap-2">
              <div className="border border-border rounded-md px-3 py-2 bg-card" data-testid="nudge-log-stat-total">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Total Nudges</p>
                <p className="text-xl font-bold font-[Chivo]">{nudgeLog.total}</p>
              </div>
              <div className="border border-border rounded-md px-3 py-2 bg-card" data-testid="nudge-log-stat-emails">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1"><EnvelopeSimple size={11} /> Emails sent</p>
                <p className="text-xl font-bold font-[Chivo]">{nudgeLog.email_sent}</p>
              </div>
              <div className="border border-border rounded-md px-3 py-2 bg-card" data-testid="nudge-log-stat-sms">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1"><DeviceMobile size={11} /> SMS sent</p>
                <p className="text-xl font-bold font-[Chivo]">{nudgeLog.sms_sent}</p>
              </div>
              <div className="border border-border rounded-md px-3 py-2 bg-card" data-testid="nudge-log-stat-open-rate">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1"><EnvelopeOpen size={11} /> Open rate</p>
                <p className="text-xl font-bold font-[Chivo]">{nudgeLog.open_rate}%</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Label htmlFor="nudge-log-days" className="text-xs text-muted-foreground">Last</Label>
              <Select value={String(nudgeLogDays)} onValueChange={(v) => setNudgeLogDays(Number(v))}>
                <SelectTrigger className="w-[110px] h-8 text-xs" data-testid="nudge-log-range"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">24 hours</SelectItem>
                  <SelectItem value="7">7 days</SelectItem>
                  <SelectItem value="14">14 days</SelectItem>
                  <SelectItem value="30">30 days</SelectItem>
                  <SelectItem value="90">90 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Table */}
          <div className="border border-border rounded-md bg-card overflow-hidden" data-testid="nudge-log-table-wrap">
            {nudgeLogLoading ? (
              <p className="px-4 py-10 text-xs text-muted-foreground text-center">Loading…</p>
            ) : nudgeLog.rows.length === 0 ? (
              <p className="px-4 py-10 text-xs text-muted-foreground text-center" data-testid="nudge-log-empty">
                No nudges in the last {nudgeLog.days} day{nudgeLog.days === 1 ? "" : "s"}. Use the bell button on the Tomorrow's Jobs at Risk widget to send one.
              </p>
            ) : (
              <table className="w-full text-left" data-testid="nudge-log-table">
                <thead>
                  <tr className="border-b border-border bg-muted/40">
                    <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">When</th>
                    <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Job</th>
                    <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Recipient</th>
                    <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Channels</th>
                    <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Email opened?</th>
                    <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Nudged by</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {nudgeLog.rows.map((r) => {
                    const when = r.created_at ? new Date(r.created_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "";
                    const opened = !!r.first_opened_at;
                    return (
                      <tr key={r.id} className="row-hover" data-testid={`nudge-log-row-${r.id}`}>
                        <td className="px-3 py-2 text-xs whitespace-nowrap">{when}</td>
                        <td className="px-3 py-2 text-xs">
                          <Link to={`/jobs/${r.job_id}`} className="text-primary hover:underline font-mono text-[11px]">{r.job_reference || r.job_id?.slice(0, 8)}</Link>
                          <p className="text-[10px] text-muted-foreground truncate max-w-[220px]">{r.job_title}</p>
                        </td>
                        <td className="px-3 py-2 text-xs">
                          <p className="font-medium truncate max-w-[160px]">{r.recipient_name || "—"}</p>
                          <p className="text-[10px] text-muted-foreground capitalize">{r.recipient_kind || ""}</p>
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            {r.email_sent ? (
                              <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" title={r.recipient_email}>
                                <EnvelopeSimple size={10} weight="fill" /> Email
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400" title={r.email_error || "Not sent"}>
                                <EnvelopeSimple size={10} /> No email
                              </span>
                            )}
                            {r.sms_sent ? (
                              <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300" title={r.recipient_phone}>
                                <DeviceMobile size={10} weight="fill" /> SMS
                              </span>
                            ) : r.recipient_phone ? (
                              <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400" title={r.sms_reason || ""}>
                                <DeviceMobile size={10} /> No SMS
                              </span>
                            ) : null}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-xs" data-testid={`nudge-log-opened-${r.id}`}>
                          {!r.email_sent ? (
                            <span className="text-[11px] text-muted-foreground">—</span>
                          ) : opened ? (
                            <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700 dark:text-emerald-400 font-medium" title={r.first_opened_at}>
                              <EnvelopeOpen size={11} weight="fill" /> Opened{r.opened_count > 1 ? ` ×${r.opened_count}` : ""}
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-[11px] text-amber-600 dark:text-amber-400">
                              <EnvelopeSimple size={11} /> Not yet
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-xs">{r.nudged_by_name || "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
